/**
 * Insight pack definitions for the Pulse management insights home.
 */

import type { PulsePackAvailability } from "@/services/api/agentic-v2/types";

export type InsightPackStatus = "active" | "coming_soon";

/** Filter tag ids for the insight pack catalog. */
export type InsightPackTagId =
  | "cse"
  | "workstack"
  | "pipeline-metrics"
  | "paraplanning"
  | "outreach"
  | "compliance"
  | "adviser-book"
  | "trials";

export const INSIGHT_PACK_TAG_OPTIONS: ReadonlyArray<{
  id: InsightPackTagId | "all";
  label: string;
}> = [
  { id: "all", label: "All" },
  { id: "cse", label: "CSE" },
  { id: "workstack", label: "Workstack" },
  { id: "pipeline-metrics", label: "Pipeline Metrics" },
  { id: "paraplanning", label: "Paraplanning" },
  { id: "outreach", label: "Outreach" },
  { id: "compliance", label: "Compliance" },
  { id: "adviser-book", label: "Adviser Book" },
  { id: "trials", label: "Trials" },
];

const INSIGHT_PACK_TAG_LABELS = Object.fromEntries(
  INSIGHT_PACK_TAG_OPTIONS.filter((tag) => tag.id !== "all").map((tag) => [
    tag.id,
    tag.label,
  ]),
) as Record<InsightPackTagId, string>;

export function insightPackTagLabel(tagId: InsightPackTagId): string {
  return INSIGHT_PACK_TAG_LABELS[tagId];
}

/** ``workstack`` packs read Plannr-derived views; ``mi`` packs read MI dashboard snapshots. */
export type InsightPackKind = "workstack" | "mi";

export interface InsightPack {
  id: string;
  name: string;
  description: string;
  status: InsightPackStatus;
  kind: InsightPackKind;
  /** Pulse V2 API interface name — views and dashboards are filtered by this. */
  pulseInterface: string;
  /** Filter tags shown on the catalog and used for tag chips. */
  tags: InsightPackTagId[];
  /** ``IconPicker`` name — see ``PREDEFINED_ICONS``. */
  icon: string;
  /** Hex colour for pack badge. */
  color: string;
  surfaceCount?: number;
}

export const OCTOPUS_CSE_WORKSTACK_PACK_ID = "octopus-cse-workstack";

export const FILE_CHECKING_MI_PACK_ID = "file-checking-mi";

export const AML_MI_PACK_ID = "aml-financial-crime-mi";

export const MEETING_OBSERVATIONS_MI_PACK_ID = "meeting-observations-mi";

export const FCA_SUPERVISION_MI_PACK_ID = "fca-supervision-mi";

export const DATA_GATHERING_MI_PACK_ID = "data-gathering-mi";

/** MI packs built on demand from live data — the pack view offers a Refresh button. */
export const REFRESHABLE_MI_PACK_IDS: ReadonlySet<string> = new Set([DATA_GATHERING_MI_PACK_ID]);

export const INSIGHT_PACKS: InsightPack[] = [
  {
    id: DATA_GATHERING_MI_PACK_ID,
    name: "Data Gathering",
    description:
      "Every household in DG with advice not yet held — checklist completeness, who's marked done, and what's still needed before the narrative.",
    status: "active",
    kind: "mi",
    pulseInterface: "Data Gathering",
    tags: ["paraplanning"],
    icon: "filter",
    color: "#10b981",
    surfaceCount: 3,
  },
  {
    id: FCA_SUPERVISION_MI_PACK_ID,
    name: "Supervision & Authorisations MI",
    description:
      "Contact volumes, consumer outcomes, application quality and oversight of Ani's recommendations.",
    status: "active",
    kind: "mi",
    pulseInterface: "Supervision & Authorisations MI",
    tags: ["compliance"],
    icon: "eye",
    color: "#6366f1",
    surfaceCount: 4,
  },
  {
    id: FILE_CHECKING_MI_PACK_ID,
    name: "File Checking MI",
    description:
      "File grades, the most common problem-statement failings and where each adviser needs support.",
    status: "active",
    kind: "mi",
    pulseInterface: "File Checking MI",
    tags: ["compliance"],
    icon: "book",
    color: "#f59e0b",
    surfaceCount: 3,
  },
  {
    id: AML_MI_PACK_ID,
    name: "AML & Financial Crime MI",
    description:
      "AML healthchecks, ID&V exceptions and financial crime findings from file checks — for the MLRO and CRO.",
    status: "active",
    kind: "mi",
    pulseInterface: "AML & Financial Crime MI",
    tags: ["compliance"],
    icon: "eye",
    color: "#ef4444",
    surfaceCount: 3,
  },
  {
    id: MEETING_OBSERVATIONS_MI_PACK_ID,
    name: "Meeting Observations MI",
    description:
      "Vulnerability, sentiment, observations and review coverage from meeting intelligence — Consumer Duty signals for the CRO.",
    status: "active",
    kind: "mi",
    pulseInterface: "Meeting Observations MI",
    tags: ["compliance"],
    icon: "binoculars",
    color: "#f43f5e",
    surfaceCount: 6,
  },
  {
    id: OCTOPUS_CSE_WORKSTACK_PACK_ID,
    name: "CSE Workstack",
    description:
      "Households, workflow plans, and DG status across your CSE roster.",
    status: "active",
    kind: "workstack",
    pulseInterface: "CSE Workstack",
    tags: ["cse", "workstack"],
    icon: "list",
    color: "#6366f1",
    surfaceCount: 5,
  },
  {
    id: "proactive-campaigns",
    name: "Proactive campaigns",
    description: "Outbound campaign lists and follow-up workqueues.",
    status: "active",
    kind: "workstack",
    pulseInterface: "Proactive campaigns",
    tags: ["outreach"],
    icon: "rocket",
    color: "#10b981",
    surfaceCount: 2,
  },
  {
    id: "paraplanning-workstack",
    name: "ParaPlanning Workstack",
    description: "Case progress, suitability checks, and paraplanning handoffs.",
    status: "active",
    kind: "workstack",
    pulseInterface: "ParaPlanning Workstack",
    tags: ["paraplanning", "workstack"],
    icon: "book",
    color: "#8b5cf6",
    surfaceCount: 3,
  },
  {
    id: "pipeline-reporting",
    name: "Pipeline Reporting",
    description: "Pipeline stages, conversion, and funnel reporting.",
    status: "active",
    kind: "workstack",
    pulseInterface: "Pipeline Reporting",
    tags: ["pipeline-metrics"],
    icon: "chart-bar",
    color: "#06b6d4",
    surfaceCount: 8,
  },
  {
    id: "advisor-portfolios",
    name: "Advisor Portfolios",
    description: "Adviser book views and portfolio-level caseload.",
    status: "active",
    kind: "workstack",
    pulseInterface: "Advisor Portfolios",
    tags: ["adviser-book"],
    icon: "building",
    color: "#3b82f6",
    surfaceCount: 3,
  },
  {
    id: "lj-work",
    name: "LJ work",
    description: "LJ team workstack and case tracking.",
    status: "active",
    kind: "workstack",
    pulseInterface: "LJ work",
    tags: ["workstack"],
    icon: "bookmark",
    color: "#ec4899",
    surfaceCount: 4,
  },
  {
    id: "re-engagement",
    name: "Re-Engagement",
    description: "Re-engagement cohorts and outreach priorities.",
    status: "active",
    kind: "workstack",
    pulseInterface: "Re-Engagement",
    tags: ["outreach"],
    icon: "comment",
    color: "#f59e0b",
    surfaceCount: 1,
  },
  {
    id: "throughput",
    name: "Throughput",
    description: "Team throughput, capacity, and flow metrics.",
    status: "active",
    kind: "workstack",
    pulseInterface: "Throughput",
    tags: ["pipeline-metrics"],
    icon: "chart-pie",
    color: "#14b8a6",
    surfaceCount: 2,
  },
  {
    id: "tc-metrics",
    name: "T&C Metrics",
    description: "Treats & compliance monitoring and exception queues.",
    status: "active",
    kind: "workstack",
    pulseInterface: "T&C Metrics",
    tags: ["compliance", "pipeline-metrics"],
    icon: "eye",
    color: "#ef4444",
    surfaceCount: 1,
  },
  {
    id: "afp-trial",
    name: "AFP Trial",
    description: "AFP trial cohort tracking and trial outcomes.",
    status: "active",
    kind: "workstack",
    pulseInterface: "AFP Trial",
    tags: ["trials", "pipeline-metrics"],
    icon: "star",
    color: "#a855f7",
    surfaceCount: 2,
  },
];

/** Packs with data for the caller's tenant. */
export function filterAvailablePacks(
  packs: InsightPack[],
  availability: PulsePackAvailability,
): InsightPack[] {
  return packs.filter((pack) =>
    pack.kind === "mi"
      ? availability.mi_pack_ids.includes(pack.id)
      : availability.workstack_data,
  );
}

export function getInsightPack(packId: string): InsightPack | undefined {
  return INSIGHT_PACKS.find((pack) => pack.id === packId);
}

export function getInsightPackWindowTitle(packId: string | null | undefined): string {
  if (!packId) return "Pulse";
  return getInsightPack(packId)?.name ?? "Pulse";
}
